import { ClockCountdownIcon, TagIcon, WarningCircleIcon } from '@phosphor-icons/react';

import { Chip } from '@/components/common/chip';
import { OverflowTooltip } from '@/components/common/overflow-tooltip';

import {
  BANNER_TITLE,
  DISCOUNT_STATUS_CHIP,
  FIRST_TIME_HELP,
  formatDetailDate,
  formatDiscountValue,
  getDiscountBanner,
  QUANTITY_RULE_LABEL,
  TRIGGER_LABEL,
} from '../format';
import type { DiscountDetail } from '../types';

import { DiscountBanner } from './discount-banner';
import styles from './discount-summary-card.module.scss';

/** Banner icon per case (Figma 4275:500664): Once Per Customer window → Tag; Limited Period →
 *  ClockCountdown (AC Case B "clock/time icon"); Offer Expired → neutral warning (no Figma frame). */
function bannerIcon(
  variant: 'limited' | 'expired',
  triggerType: DiscountDetail['trigger']['type'],
) {
  if (variant === 'expired') return <WarningCircleIcon size={14} weight="fill" />;
  if (triggerType === 'limited_period') return <ClockCountdownIcon size={14} weight="fill" />;
  return <TagIcon size={14} weight="fill" />;
}

export function DiscountSummaryCard({ discount }: { discount: DiscountDetail }) {
  const chip = DISCOUNT_STATUS_CHIP[discount.status];

  const banner = getDiscountBanner({ status: discount.status, trigger: discount.trigger });
  return (
    <section className={styles.card}>
      <div className={styles.metaRow}>
        <Chip intent={chip.intent}>{chip.label}</Chip>
        <span className={styles.createdOn}>
          Created on{' '}
          <span className={styles.createdDate}>{formatDetailDate(discount.createdDate)}</span>
        </span>
      </div>
      <div className={styles.valueBlock}>
        <p className={styles.value}>
          {formatDiscountValue(discount.value, discount.discountFormat)}
        </p>
        <p className={styles.trigger}>{TRIGGER_LABEL[discount.trigger.type]}</p>
        {discount.trigger.type === 'first_time_customer' ? (
          <p className={styles.help}>{FIRST_TIME_HELP}</p>
        ) : null}
      </div>

      <div className={styles.field}>
        <span className={styles.label}>Quantity Rule</span>
        <span className={styles.fieldValue}>{QUANTITY_RULE_LABEL[discount.quantityRule.type]}</span>
      </div>

      <div className={styles.field}>
        <span className={styles.label}>Applicable To</span>
        <span className={styles.fieldValue}>{discount.applicability.summary}</span>
      </div>

      {discount.description ? (
        <div className={styles.field}>
          <span className={styles.label}>Description</span>
          {/* Clamped to 3 lines + tooltipped so a description near the 2000-char max still has
              its full text reachable (117327). */}
          <OverflowTooltip title={discount.description}>
            <p className={styles.description}>{discount.description}</p>
          </OverflowTooltip>
        </div>
      ) : null}

      {banner ? (
        <DiscountBanner
          tone={banner.variant === 'expired' ? 'muted' : 'lime'}
          icon={bannerIcon(banner.variant, discount.trigger.type)}
          title={BANNER_TITLE[banner.variant]}
          description={banner.description}
        />
      ) : null}
    </section>
  );
}
